/**
 * BoomView (live view) wire protocol, shared by the applicant app (recorder)
 * and the portal (viewer). communication-service relays these events on the
 * `/live-view` socket.io namespace and never parses the rrweb payloads.
 */

export const LIVE_VIEW_NAMESPACE = "/live-view";

/** How often the applicant app re-announces its presence and current step. */
export const PRESENCE_INTERVAL_MS = 20_000;
/** How often the recorder flushes buffered rrweb events. */
export const FLUSH_INTERVAL_MS = 500;
/**
 * Max characters of serialized rrweb events per socket message. The server
 * caps each message at 512 KiB measured in UTF-8 bytes; a JS character is at
 * most 3 bytes (surrogate pairs are 2 characters for 4 bytes), so 128 Ki
 * characters stays under the cap even for all non-ASCII text.
 */
export const MAX_CHUNK_CHARS = 128 * 1024;
/** After the last viewer disconnects, the recorder waits this long before stopping. */
export const VIEWER_LEFT_GRACE_MS = 60_000;
/** The viewer plays this far behind the newest event so playback stays smooth. */
export const PLAYBACK_BUFFER_MS = 1_500;

/** The applicant is always asked first (BSCPF-7033: no session without approval). */
export type ConsentMode = "ask";

export type LiveViewTicketResponse =
  | { enabled: false }
  | {
      enabled: true;
      token: string;
      expires_in: number;
      /** Applicant tickets only. */
      consent_mode?: ConsentMode;
    };

/**
 * Presence-only ticket for list pages (the portal applications table): it can
 * receive presence for up to MAX_PRESENCE_APPLICATIONS applications but can't
 * request, join or stop sessions.
 */
export type LiveViewPresenceTicketResponse =
  | { enabled: false }
  | {
      enabled: true;
      token: string;
      expires_in: number;
      /** The requested ids the user may see presence for. */
      application_ids: number[];
    };

export const MAX_PRESENCE_APPLICATIONS = 100;

export interface PresenceEntry {
  application_id: number;
  applicant_id: number;
  applicant_name: string;
  /** Human label for where the applicant is, e.g. "Income". */
  step: string;
  path: string;
  /** ISO timestamp of the last presence update. */
  updated_at: string;
}

export interface PresenceSnapshot {
  applicants: PresenceEntry[];
}

export interface PresenceLeft {
  application_id: number;
  applicant_id: number;
}

export type SessionStatus =
  | "requested"
  | "active"
  | "paused"
  | "declined"
  /** The applicant left or didn't answer in time; no session started. */
  | "expired"
  | "ended";

/** A pending request expires after this long without an answer. */
export const REQUEST_TIMEOUT_MS = 120_000;

export interface SessionUpdate {
  session_id: string;
  status: SessionStatus;
  by?: "applicant" | "viewer";
}

/** Sent to the applicant when a viewer asks to watch. */
export interface ViewRequest {
  session_id: string;
  viewer_name: string;
  viewer_org: string;
}

export interface SessionRef {
  session_id: string;
}

export interface EventsPayload {
  session_id: string;
  /** Monotonic per recorder, for debugging and ordering checks. */
  seq: number;
  /** Present when one batch was split across several messages. */
  chunk?: { id: string; index: number; count: number };
  /** JSON-encoded rrweb event array, or one slice of it when chunked. */
  data: string;
}

export type Ack<T extends object = object> =
  | ({ ok: true } & T)
  | { ok: false; error: string };

export const LiveViewEvents = {
  /** applicant → server, and server → viewers */
  Presence: "presence",
  /** applicant → server */
  Respond: "respond",
  /** applicant → server */
  Resume: "resume",
  /** applicant → server, and server → viewers */
  Events: "events",
  /** applicant or viewer → server */
  Stop: "stop",
  /** viewer → server, and server → applicant */
  Request: "request",
  /** viewer → server */
  Join: "join",
  /** server → viewer, on connect */
  PresenceSnapshot: "presence:snapshot",
  /** server → viewers */
  PresenceLeft: "presence:left",
  /** server → applicant */
  SnapshotRequest: "snapshot_request",
  /** server → applicant */
  ViewerLeft: "viewer_left",
  /** server → applicant and viewers */
  Session: "session"
} as const;
